import { setIsNewSearch, setPersonalizedSearch } from '@shared/redux/actions';
import {
  messageErrorDates,
  messageErrorDollar,
  messageErrorFields,
  messageErrorPaices,
} from '@shared/helpers/messages';
import { postMetaConverter } from '@shared/services/meta';
import moment from 'moment';

const eventNameMap = {
  OneWay: 'busquedaSoloIda',
  RoundTrip: 'busquedaIdaYVuelta',
  MultipleFlights: 'busquedaMultiDestino',
};

export const validateLegs = (legs, isInUSD) => {
  if (!legs || legs.length === 0) {
    return { valid: false, message: messageErrorFields };
  }

  const today = moment().startOf('day');

  const hasMissingFields = legs.some(f => {
    const isInvalidDate =
      !f.date ||
      f.date === 'Invalid date' ||
      !moment(f.date, 'YYYY/MM/DD', true).isValid();

    const dateObj = moment(f.date, 'YYYY/MM/DD', true);

    const isPastDate = dateObj.isValid() && dateObj.isBefore(today, 'day'); // 👈 NUEVO

    const missing =
      !f.origin?.iata_code ||
      !f.destination?.iata_code ||
      isInvalidDate ||
      isPastDate;

    return missing;
  });

  if (hasMissingFields) {
    return { valid: false, message: messageErrorFields };
  }

  const hasSameOriginDest = legs.some(f => {
    const same =
      f.origin?.iata_code === f.destination?.iata_code ||
      f.origin?.label === f.destination?.label;
    return same;
  });

  if (hasSameOriginDest) {
    return { valid: false, message: messageErrorPaices };
  }

  // Validar orden cronológico entre tramos
  if (legs.length > 1) {
    for (let i = 1; i < legs.length; i++) {
      const prevDate = moment(legs[i - 1].date, 'YYYY/MM/DD');
      const currentDate = moment(legs[i].date, 'YYYY/MM/DD');
      if (
        !currentDate.isAfter(prevDate, 'day') &&
        currentDate.format('YYYY/MM/DD') != prevDate.format('YYYY/MM/DD')
      ) {
        return { valid: false, message: messageErrorDates };
      }
    }
  }

  // No permitir USD en vuelos domésticos
  const allDomestic = legs.every(
    f =>
      f.origin?.country === 'Argentina' &&
      f.destination?.country === 'Argentina'
  );

  if (allDomestic && isInUSD) {
    return { valid: false, message: messageErrorDollar };
  }

  return { valid: true };
};

const updateAirSectionDate = (airSectionRow, index, newDate) => {
  if (!newDate || !airSectionRow[index]) return airSectionRow;
  const updated = [...airSectionRow];
  updated[index] = { ...updated[index], date: newDate };
  return updated;
};

export const prepareLegs = ({
  airSectionRow = [],
  new_go_date,
  new_back_date,
}) => {
  // Actualiza las fechas de salida y regreso si se pasan nuevas
  let updatedAirSectionRow = updateAirSectionDate(
    airSectionRow,
    0,
    new_go_date
  );
  updatedAirSectionRow = updateAirSectionDate(
    updatedAirSectionRow,
    1,
    new_back_date
  );

  return updatedAirSectionRow.map(el => {
    const origin = el.origin || {};
    const destination = el.destination || {};
    let date = '';

    if (el.date) {
      const rawDate =
        typeof el.date === 'string'
          ? el.date
          : el.date.date_selected || el.date;

      // ⚙️ Acá la conversión al formato con /
      date = moment(rawDate, ['YYYY-MM-DD', 'YYYY/MM/DD']).format('YYYY/MM/DD');
    }

    return { origin, destination, date };
  });
};

export const flightsSearchHandleFetch = ({
  flightClass,
  searchPax,
  isInUSD,
  airSectionRow,
  setHasErrorMessage,
  dispatch,
  user,
  searchType,
  new_go_date = null,
  new_back_date = null,
}) => {
  // 1️⃣ Preparar los legs
  const legs = prepareLegs({ airSectionRow, new_go_date, new_back_date });

  // 2️⃣ Resetear resultados personalizados
  dispatch(
    setPersonalizedSearch({
      searchFlightsResultPersonalizedOneWay: null,
      searchFlightsResultPersonalizedRoundTrip: null,
      searchFlightsResultPersonalizedMultipleDestination: null,
    })
  );

  // 3️⃣ Validar legs
  const { valid, message } = validateLegs(legs, isInUSD);

  if (!valid) {
    setHasErrorMessage(message);
    return false;
  }

  // 4️⃣ Datos para analítica
  const firstLeg = legs[0];
  const secondLeg = legs[1];
  const origen = firstLeg?.origin?.label || firstLeg?.origin?.code;
  const destino = firstLeg?.destination?.label || firstLeg?.destination?.code;
  const desde = firstLeg?.date;
  const hasta = secondLeg?.date ?? null;

  const value = {
    origen,
    destino,
    desde,
    hasta,
    tipo_de_cabina: flightClass?.toString(),
    cantidad_pasajeros: searchPax.adults + searchPax.childs.length,
    is_in_usd: isInUSD,
  };

  const eventName = eventNameMap[searchType] || 'busquedaDesconocida';
  window.dataLayer.push({
    event: eventName,
    eventProps: {
      category: 'vuelos',
      action: 'search',
      label: 'buscar',
      value,
    },
  });

  postMetaConverter(
    {
      event_name: eventName,
      custom_data: {
        category: 'vuelos',
        action: 'search',
        label: 'buscar',
        ...value,
      },
    },
    user
  );

  // 5️⃣ Flag de nueva búsqueda
  dispatch(
    setIsNewSearch({ isNewSearch: true, originalSearchType: searchType })
  );

  // 6️⃣ Retornamos body listo para enviar
  const body = {
    id_integration: 2,
    adults_qty: searchPax.adults,
    childs: searchPax?.childs ?? [],
    trip_type: searchType,
    dataTripLegs: legs,
    cabin_type: flightClass.toString(),
    chosen_airlines: [],
    flexible_date: false,
    is_in_usd: isInUSD,
  };

  return body;
};
